import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Button } from './UIComponents';
import { Check, ChevronsUpDown, Search, Languages } from 'lucide-react';

export type TranslationDisplayMode = 'original' | 'translation' | 'bilingual';
// ... (rest of the imports/types)

interface LanguageOption {
  value: string;
  label: string;
}

interface TranslationSettingsProps {
  targetLanguage: string;
  onTargetLanguageChange: (value: string) => void;
  displayMode: TranslationDisplayMode;
  onDisplayModeChange: (mode: TranslationDisplayMode) => void;
  onTranslate: () => void;
  isTranslating?: boolean;
  translationProgress?: number;
  hasTranslation?: boolean;
  isTranslationStale?: boolean;
  errorMessage?: string | null;
  languageOptions: LanguageOption[];
}

const DISPLAY_MODES: Array<{ value: TranslationDisplayMode; label: string; helper: string }> = [
  { value: 'original', label: 'Original', helper: 'Original text only' },
  { value: 'translation', label: 'Translation', helper: 'Translated text only' },
  { value: 'bilingual', label: 'Bilingual', helper: 'Original + translation' },
];

const TranslationSettings: React.FC<TranslationSettingsProps> = ({
  targetLanguage,
  onTargetLanguageChange,
  displayMode,
  onDisplayModeChange,
  onTranslate,
  isTranslating = false,
  translationProgress,
  hasTranslation = false,
  isTranslationStale = false,
  errorMessage,
  languageOptions,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return languageOptions;
    const lowerTerm = searchTerm.toLowerCase();
    return languageOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(lowerTerm) ||
        option.value.toLowerCase().includes(lowerTerm)
    );
  }, [languageOptions, searchTerm]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
    setHighlightedIndex(0);
  }, [isOpen]);

  useEffect(() => {
    // Scroll highlighted item into view
    if (isOpen && listRef.current) {
      const highlightedItem = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedItem) {
        highlightedItem.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  const handleSelect = (value: string) => {
    onTargetLanguageChange(value);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions[highlightedIndex]) {
        handleSelect(filteredOptions[highlightedIndex].value);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const selectedLabel = languageOptions.find(opt => opt.value === targetLanguage)?.label || 'Select language...';

  return (
    <div className="w-80 rounded-lg border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-xl p-4 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Translation Settings</h3>
        <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">Choose a target language and display mode.</p>
      </div>

      <div className="space-y-2" ref={wrapperRef}>
        <label className="text-xs font-medium text-slate-600 dark:text-zinc-400">
          Target Language
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between rounded-md border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-slate-800 dark:text-zinc-200 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-800"
          >
            <span className="truncate">{selectedLabel}</span>
            <ChevronsUpDown className="h-4 w-4 text-slate-400 dark:text-zinc-500 shrink-0" />
          </button>

          {isOpen && (
            <div className="absolute z-10 mt-1 w-full rounded-md bg-white dark:bg-zinc-800 shadow-lg ring-1 ring-black dark:ring-zinc-700 ring-opacity-5 focus:outline-none animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-150">
              <div className="p-2 border-b border-slate-100 dark:border-zinc-700">
                <div className="relative">
                  <Search className="absolute start-2 top-2.5 h-3.5 w-3.5 text-slate-400 dark:text-zinc-500" />
                  <input
                    ref={inputRef}
                    type="text"
                    className="w-full rounded-md border border-slate-200 dark:border-zinc-700 bg-slate-50 dark:bg-zinc-900 py-1.5 ps-8 pe-3 text-xs text-slate-900 dark:text-zinc-200 placeholder-slate-500 dark:placeholder-zinc-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    placeholder="Search languages..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setHighlightedIndex(0);
                    }}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </div>
              <ul
                ref={listRef}
                className="max-h-60 overflow-auto py-1 text-sm focus:outline-none"
              >
                {filteredOptions.length === 0 ? (
                  <li className="relative cursor-default select-none py-2 px-3 text-slate-500 dark:text-zinc-400 text-xs text-center">
                    No languages found.
                  </li>
                ) : (
                  filteredOptions.map((option, index) => (
                    <li
                      key={option.value}
                      className={`relative cursor-default select-none py-2 ps-3 pe-9 ${
                        index === highlightedIndex
                          ? 'bg-indigo-600 text-white'
                          : 'text-slate-900 dark:text-zinc-200 hover:bg-slate-50 dark:hover:bg-zinc-700'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.nativeEvent.stopImmediatePropagation();
                        handleSelect(option.value);
                      }}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      <span className={`block truncate ${targetLanguage === option.value ? 'font-semibold' : 'font-normal'}`}>
                        {option.label}
                      </span>
                      {targetLanguage === option.value && (
                        <span
                          className={`absolute inset-y-0 end-0 flex items-center pe-4 ${
                            index === highlightedIndex ? 'text-white' : 'text-indigo-600'
                          }`}
                        >
                          <Check className="h-4 w-4" />
                        </span>
                      )}
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <span className="text-xs font-medium text-slate-600 dark:text-zinc-400">Display Mode</span>
        <div className="grid grid-cols-3 gap-1">
          {DISPLAY_MODES.map((mode) => (
            <button
              key={mode.value}
              type="button"
              onClick={() => onDisplayModeChange(mode.value)}
              className={`rounded-md border px-0.5 py-2 text-[10px] sm:text-xs font-medium text-center transition-colors flex items-center justify-center min-w-0 ${
                displayMode === mode.value
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400'
                  : 'border-slate-200 dark:border-zinc-700 text-slate-600 dark:text-zinc-400 hover:border-slate-300 dark:hover:border-zinc-600 hover:bg-slate-50 dark:hover:bg-zinc-800'
              }`}
              title={mode.helper}
            >
              <span className="truncate">{mode.label}</span>
            </button>
          ))}
        </div>
        {!hasTranslation && (
          <p className="text-xs text-slate-400 dark:text-zinc-500">Translation will appear once available.</p>
        )}
      </div>

      {errorMessage && (
        <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md px-3 py-2">
          {errorMessage}
        </div>
      )}

      <div className="flex justify-center pt-2">
        {/* Button states:
            1. Translating: Show progress (e.g., "Translating 45%")
            2. Translated & not stale: Show "Translated" (disabled)
            3. Translated & stale: Show "Retry Translation"
            4. Not translated: Show "Translate Now"
        */}
        {isTranslating ? (
          <Button
            variant="primary"
            size="md"
            isLoading={true}
            disabled={true}
            className="w-full transition-all duration-200 shadow-md"
          >
            {translationProgress !== undefined && translationProgress > 0
              ? `Translating ${translationProgress}%`
              : 'Translating...'}
          </Button>
        ) : hasTranslation && !isTranslationStale ? (
          <Button
            variant="outline"
            size="md"
            disabled={true}
            className="w-full transition-all duration-200 opacity-60 cursor-not-allowed"
          >
            <Check className="me-2 h-4 w-4" />
            Translated
          </Button>
        ) : (
          <Button
            variant="primary"
            size="md"
            onClick={onTranslate}
            className="w-full active:scale-[0.98] transition-all duration-200 shadow-md hover:shadow-lg"
          >
            <Languages className="me-2 h-4 w-4" />
            {hasTranslation && isTranslationStale ? 'Retry Translation' : 'Translate Now'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default TranslationSettings;
